import { useTenant } from '@emerald-works-nova/auth'
import { useSession } from '@emerald-works/react-auth'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import React, { createContext, forwardRef, useContext, useEffect } from 'react'
import { ALLOW_PLAYLISTS_GROUP } from '../components/user-group-policy/permissions'
import consts from '../consts'
import { useProducts } from '../hooks'
import { accelerateSlice, campaignSlice, categorySlice, onboardingSlice, searchResultsSlice, userSlice } from '../reducers'
import useAuthEvents from './useAuthEvents'
import useGroup from './useGroup'

export const EventContext = createContext(null)

export const useEventContext = () => {
  return useContext(EventContext)
}

const EventContextProviderRef = ({ children }, ref) => {
  const { isInGroup } = useGroup()
  const tenant = useTenant()
  const { hasContentHubAccess } = useProducts()

  // const isClub = tenant.pk === consts.DEFAULT_CLUB_PK
  // const isConsumer = isInGroup(CONSUMER)
  // const isClubConsumer = isClub && isConsumer
  const allowPlaylistOnboarding = isInGroup(ALLOW_PLAYLISTS_GROUP)

  const [getProfile] = useEvent([userSlice.eventBus.getProfile])
  const [getAllBookmarkedContentIds] = useEvent([userSlice.eventBus.getAllBookmarkedContentIds])
  const [getAllRecommendedArticleIds] = useEvent([userSlice.eventBus.getAllRecommendedArticleIds])
  const [onboardingGetUser] = useEvent([onboardingSlice.eventBus.onboardingGetUser])
  const [getCategoryDefaultHierarchy] = useEvent([categorySlice.eventBus.getCategoryDefaultHierarchy])
  const [getUserSearchHistory] = useEvent([searchResultsSlice.eventBus.getUserSearchHistory])
  // const [listBonusMaterials] = useEvent([bonusMaterialSlice.eventBus.listBonusMaterials])
  const [getAccelerateStatus] = useEvent([accelerateSlice.eventBus.getAccelerateStatus])

  const [getUserLoginCount] = useEvent([{
    eventName: 'getUserLoginCount',
    onSuccess: (count) => {
      if (count > 2) {
        if (window.hj && typeof window.hj === 'function') {
          console.log('sending to hotjar: user_has_logged_in_more_than_two_times', count)
          window.hj('event', 'user_has_logged_in_more_than_two_times')
        }
      }
    }
  }])
  const [getCampaignData] = useEvent([campaignSlice.eventBus.getCampaignData])

  const { isAuthenticated, user } = useSession()

  useAuthEvents()

  useEffect(() => {
    const email = user?.attributes?.email
    if (isAuthenticated && email && tenant?.pk === consts.DEFAULT_CLUB_PK) {
      if (window.dmPt) {
        window.dmPt('identify', email)
        window.dmPt('track')
      }
    }
  }, [isAuthenticated, user, tenant])

  // useEventsOnViewLoad(() => {
  //   if (isClubConsumer && !listBonusMaterials.hasBeenTriggered) {
  //     listBonusMaterials.trigger()
  //   }
  // }, [isClubConsumer])

  const triggerContentHubEvents = () => {
    getUserSearchHistory.trigger()
    getCategoryDefaultHierarchy.trigger()
    if (allowPlaylistOnboarding) {
      onboardingGetUser.trigger()
    }
  }

  const triggerAuthenticatedEvents = () => {
    getProfile.trigger()
    getUserLoginCount.trigger()
    getAllBookmarkedContentIds.trigger()
    getAllRecommendedArticleIds.trigger()
    getAccelerateStatus.trigger()
    if (hasContentHubAccess) {
      triggerContentHubEvents()
    }
  }

  useEventsOnViewLoad(() => {
    if (isAuthenticated) triggerAuthenticatedEvents()
    if (hasContentHubAccess) getCampaignData.trigger()
  }, [
    hasContentHubAccess,
    getProfile,
    getUserSearchHistory,
    getCategoryDefaultHierarchy,
    getUserLoginCount,
    getCampaignData,
    isAuthenticated,
    onboardingGetUser,
    allowPlaylistOnboarding
  ])

  return (
    <EventContext.Provider value={{ getProfile }}>
      {children}
    </EventContext.Provider>
  )
}

export const EventContextProvider = forwardRef(EventContextProviderRef)
